import styles from "./components.module.scss";
import {MenuItem, Tooltip} from '@mui/material';
import React, {FC, useEffect, useState} from "react";
import ShiftDescriptionContainer from "./ShiftDescriptionContainer";
import MaxBookedPopup from "./MaxBookedPopup";
import {usePersistedState} from "../../../../hooks";
import {HygienistProps} from "utils/types";
import {entities} from "../../../../reduxStore";
import {exitReact} from "utils";
import useActionApi from "../../../../hooks/useActionApi";
import Api from "../../../../api";
import { JobProps } from "utils/types";
import { PartialOffer } from "utils/types"
import {MaterialSelector} from "components/Selector";
import _ from "lodash";
import moment from "moment";
import FixedLoader from "components/FixedLoader/FixedLoader";
import {ContainedButton, OutlinedButton, Description, PartialOfferSubtitle} from "../AvailableJob.styles";
import Popup from "components/Popup";
import {ShiftPopup} from "../../ShiftDetailPage/components";
import { JOB_STATUSES, MINIMUM_PARTIAL_SHIFT_HOURS, PARTIAL_OFFER_STATUS } from "utils/constants";
import MaterialInput from "components/Inputs/MaterialInput";
import MakePartialOfferPopup from "./MakePartialOffer";
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Show } from "components/Conditionals";
import { stringToDate } from "utils/time";
import InfoIcon from '@mui/icons-material/Info';
import clsx from "clsx";
// @ts-ignore
const TempStars = window.TempStars

const PARTIAL_OFFER_TIME_WINDOW = 72;
const PARTIAL_OFFER_TOOLTIP_TEXT = "Dental offices prefer booking full shifts! Partial offers only unlock when a shift remains available 72 hours before start time. Get ahead of the competition by offering to work the complete shift!";

// @ts-ignore
const isMobileApp = !!window.cordova;

const CalculateShiftRateOptions = (hygienist: HygienistProps, job: JobProps): JSX.Element[] => {
    const last = (array: number[]) => array[array.length - 1];
    const getArrayInRange = (min: number, max: number) => _.range(min, max + 1);

    let options: JSX.Element[] = [
        <MenuItem key={'noValue'} disabled>Select Hourly Rate</MenuItem>
    ];

    let proposedRate = job.proposedRate ?? job.avgRate;

    const greenRate = getArrayInRange(proposedRate - 5, proposedRate + 2);
    let yellowRate = getArrayInRange(last(greenRate) + 1, last(greenRate) + 4);
    const redRate: number[] = [];
    const isSaturdayShift = moment(job.startDate).format('dddd') === 'Saturday';

    if (isSaturdayShift || hygienist.status === 1 || hygienist.status === 2) {
        yellowRate.push(...getArrayInRange(last(yellowRate) + 1, last(yellowRate) + 3));
    }

    if (hygienist.status === 2) {
        redRate.push(...getArrayInRange(last(yellowRate) + 1, last(yellowRate) + 3));
    }

    const allRates = [...greenRate, ...yellowRate, ...redRate];
    const allRateItems = allRates.map(rate => (
        <MenuItem key={rate} className={styles.rateMenuItem} value={rate}>
            {rate === proposedRate ? (
                <div className={styles.rateSuggested}><b>${rate}/hr</b> Suggested Rate</div>
            ) : `$${rate}/hr`}
        </MenuItem>
    ));

    options.push(...allRateItems);

    if (!isSaturdayShift && hygienist.status === 0) {

        const disabledRates = getArrayInRange(last(allRates) + 4, last(allRates) + 6).map(rate => (
            <MenuItem key={rate} className={styles.rateMenuItem} disabled value={rate}>
                {`$${rate}/hr (Earn "Elite" Status to Unlock)`}
            </MenuItem>
        ));

        const additionalDisabledRates = getArrayInRange(last(allRates) + 1, last(allRates) + 3).map(rate => (
            <MenuItem key={rate} className={styles.rateMenuItem} disabled value={rate}>
                {`$${rate}/hr (Earn "Elite" Status to Unlock)`}
            </MenuItem>
        ));

        options.push(...disabledRates, ...additionalDisabledRates);
    }

    if (!isSaturdayShift && hygienist.status === 1) {
        const disabledRates = getArrayInRange(last(allRates) + 1, last(allRates) + 3).map(rate => (
            <MenuItem key={rate} className={styles.rateMenuItem} disabled value={rate}>
                {`$${rate}/hr (Earn "Elite" Status to Unlock)`}
            </MenuItem>
        ));
        options.push(...disabledRates);
    }

    return options;
};

interface MakeAnOfferProps {
    hygienist: HygienistProps,
    job: JobProps,
    proposedRate: number
    setProposedRate: (val: number) => void
    isOpenOfferShiftPopup: boolean
    setIsOpenOfferShiftPopup: (val: boolean) => void
    isMaxBookedPopupOpen: boolean
    setIsMaxBookedPopupOpen: (val: boolean) => void
    onClose: () => void
}

interface MakePartialOfferProps {
    hygienist: HygienistProps,
    job: JobProps,
    onClose: () => void,
    proposedRate: number,
    setProposedRate: (val: number) => void,
    isOpenOfferShiftPopup: boolean,
    setIsOpenOfferShiftPopup: (val: boolean) => void,
    isMaxBookedPopupOpen: boolean,
    setIsMaxBookedPopupOpen: (val: boolean) => void,
}

interface OfferShiftPopupProps {
    proposedRate: number,
    onSubmit: (rate: number) => void
}

const MakeAnOfferPopup:FC<MakeAnOfferProps> = ({hygienist, job, proposedRate, setProposedRate, setIsOpenOfferShiftPopup, isMaxBookedPopupOpen, setIsMaxBookedPopupOpen, onClose}) => {
    const [isMaxBookedOffers, setIsMaxBookedOffers] = useState<boolean>(false);
    const [isMaxConfirmedJobs, setIsMaxConfirmedJobs] = useState<boolean>(false);

    const [getHygienistOffersLoading, , getHygienistOffers] = useActionApi({
        apiMethod: Api.getHygienistOffers,
        onSuccess: (offers: PartialOffer[]) => {
            const postedOffers = offers.filter(offer => offer.status === PARTIAL_OFFER_STATUS.POSTED)
            setIsMaxBookedOffers(postedOffers.length >= hygienist?.maxBooked * 2);
        },
        onError: (error) => {
            console.error('Error fetching hygienist offers:', error);
            throw error;
        }
    });

    const [getHygienistJobsLoading, , getHygienistJobs] = useActionApi({
        apiMethod: Api.getHygienistJobs,
        onSuccess: (jobs: JobProps[]) => {
            const confirmedJobs = jobs.filter(job => job.status === JOB_STATUSES.CONFIRMED)
            setIsMaxConfirmedJobs(confirmedJobs.length >= hygienist?.maxBooked);
        },
        onError: (error) => {
            console.error('Error fetching hygienist jobs:', error);
            throw error;
        }
    });

    useEffect(() => {
        getHygienistJobs('');
        getHygienistOffers({});
    }, []);

  const onOpenOfferShiftPopup = async() => {
    if (isMaxBookedOffers || isMaxConfirmedJobs) {
        setIsMaxBookedPopupOpen(true);
        onClose()
        return;
    }
    onClose()
    setIsOpenOfferShiftPopup(true)
  }

    return (
        <div className={styles.popupContainer}>
          <div className={styles.titleContainer}>
            <h2 className={styles.popupTitle}>Offer to Work this Shift?</h2>
            <p className={styles.popupSubtitle}>
                READ CAREFULLY
            </p>
          </div>


          <p className={styles.modalDescription}>
            Submit an Offer to work this shift by selecting your hourly rate.
          </p>
          <p className={styles.modalDescription}>
            We will immediately forward your Offer to {job.location.practiceName} for you. An Accepted Offer is a binding commitment to work the shift.
          </p>
          <p className={styles.modalDescription}>
            They will review your Offer and we'll advise you within 18hrs if your Offer is Accepted.
          </p>
          <p className={styles.modalDescription}>
            <span className={styles.emphasizedText}>Remember: Submit an offer only if you can commit to the shift upon acceptance.</span> If your offer is Accepted, this means you are Booked & Confirmed for this shift, and have an important commitment to keep.
          </p>

          <div className={styles.selectorTitle}>
            Offer your hourly rate.
          </div>

          <MaterialSelector
            className={styles.styledSelector}
            name="proposedRate"
            label="Select hourly rate"
            value={proposedRate}
            onChange={(e: any) => setProposedRate(e.target.value)}
            errorMessage={proposedRate ? '' : 'Please select a rate'}
          >
            {!!(job?.ghr) ? (
              <MenuItem key={'isGuaranteedRate'} value={job.proposedRate ?? job.avgRate}>
                ${job.ghr}/hr Guaranteed
              </MenuItem>
            ) : CalculateShiftRateOptions(hygienist, job)}
          </MaterialSelector>
          <ContainedButton
            className={clsx(styles.modalButton, 'no-fastclick')}
            variant="contained"
            size="large"
            onClick={onOpenOfferShiftPopup}
          >
            Submit
          </ContainedButton>
          <OutlinedButton
            className={clsx(styles.modalButton, 'no-fastclick')}
            variant="outlined"
            size="large"
            onClick={onClose}
          >
            Cancel
          </OutlinedButton>

          <FixedLoader
            loading={getHygienistOffersLoading || getHygienistJobsLoading}
        />
        </div>
    );
}

interface UpdateCDHOCertificateProps {
    hygienist: HygienistProps,
    onClose: () => void
}

const UpdateCDHOCertificate:FC<UpdateCDHOCertificateProps> = ({hygienist, onClose}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [CDHONumber, setCDHONumber] = useState<string>('');

    const onUpdateCDHOCertificate = async () => {
        setLoading(true)
        try {
            await Api.updateHygienistAccount({
                CDHONumber,
            })
            hygienist.CDHONumber = Number(CDHONumber)
        } catch (error) {
            console.error('Error updating hygienist account:', error);
            throw error;
        } finally {
            setLoading(false)
            onClose()
        }
    }

    const onChangeCDHONumber = (e: any) => {
        setCDHONumber(e.target.value)
    }

    return (
        <>
        <div className={styles.popupContainer}>
          <div className={styles.titleContainer}>
            <h2 className={styles.popupTitle}>Please Update Your Certificate Number</h2>
            <p className={styles.popupSubtitle}>
                READ CAREFULLY
            </p>
          </div>


          <p className={styles.modalDescription}>
          With the recent changes to the requirements for Level 2 Dental Assistants, we now require your NDABE certificate number to be on your profile before you can submit an offer.
          </p>

          <MaterialInput
            className={styles.inputContainer}
            label="Certificate Number"
            value={CDHONumber}
            onChange={onChangeCDHONumber}
            name={"CDHONumber"}
            helperText={CDHONumber.length >= 4 ? '' : 'Please enter your Certificate Number'}
            error={CDHONumber.length < 4}
          />

          <ContainedButton
            className={clsx(styles.modalButton, 'no-fastclick')}
            variant="contained"
            size="large"
            onClick={onUpdateCDHOCertificate}
          >
            Done
          </ContainedButton>
          <OutlinedButton
            className={clsx(styles.modalButton, 'no-fastclick')}
            variant="outlined"
            size="large"
            onClick={onClose}
          >
            Cancel
          </OutlinedButton>
        </div>

        <FixedLoader
            loading={loading}
        />
        </>
    );
}

const MakeOfferModal:FC<{job: JobProps, history: any, trackData: any}> = ({job, history, trackData }) => {
    const [isOpenMakeOfferModal, setIsOpenMakeOfferModal] = useState<boolean>(false);
    const [isOpenHideOfferModal, setIsOpenHideOfferModal] = useState<boolean>(false);
    const [isOpenPartialOfferModal, setIsOpenPartialOfferModal] = useState<boolean>(false);
    const [isOpenOfferShiftPopup, setIsOpenOfferShiftPopup] = useState<boolean>(false);
    const [isOpenCDHOCertificateModal, setIsOpenCDHOCertificateModal] = useState<boolean>(false);
    const [proposedRate, setProposedRate] = useState<number>(job.proposedRate ?? job.avgRate);
    const [isMaxBookedPopupOpen, setIsMaxBookedPopupOpen] = useState<boolean>(false);
    const [hygienistStatus, setHygienistStatus] = useState<number>(0);
    const [completedShifts, setCompletedShifts] = useState<number>(0);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);

    const hygienist = usePersistedState<HygienistProps>({
        entityName: entities.hygienist
    })[0];

    const [makePartialOfferLoading, , makePartialOffer] = useActionApi({
        apiMethod: Api.makePartialOffer,
    });

    const [hideOfferLoading, , hideOffer] = useActionApi({
        apiMethod: Api.hideOffer,
        onSuccess: () => {
            setIsOpenHideOfferModal(false)
            history.goBack()
        }
    });

    const onHideOffer = async () => {
        hideOffer({jobId: job.id})
    }

    useEffect(() => {
        const fetchHygienistData = async () => {
          setHygienistStatus(hygienist?.status);
          setCompletedShifts(hygienist?.completedJobs);
        };
        fetchHygienistData();
      }, []);

    const onMakePartialOffer = async (startTime: string, endTime: string, rate: number, isPartial: boolean = false) => {
        if(isPartial) {
            setIsOpenPartialOfferModal(false)
        }
        setIsOpenMakeOfferModal(false)
        setIsOpenOfferShiftPopup(false)

        await makePartialOffer({
            hourlyRate: rate,
            jobId: job.id,
            startTime,
            endTime,
            isPartial
        })
        TempStars.Analytics.track('send offer', {
            ...trackData,
            requestedHourlyRate: rate,
            startTime,
            endTime
        });
        history.push('loading')
        exitReact('home')
    }

    const handleOfferShiftSubmit = (rate: number) => {
        onMakePartialOffer(job.shifts[0].postedStart, job.shifts[0].postedEnd, rate);
    }

    const sendOfferHandler = () => {
        if(job.location.province === 'Ontario' && hygienist?.skills?.find(skill => skill.name === 'CDA2') && !hygienist?.CDHONumber) {
            setIsOpenCDHOCertificateModal(true)
        } else {
            setIsOpenMakeOfferModal(true)
        }
    }

    const start: Date = stringToDate(job.shifts[0].postedStart);
    const end: Date = stringToDate(job.shifts[0].postedEnd);

    const diffMs = end.getTime() - start.getTime();
    const hoursToMs = (hours: number) => hours * 60 * 60 * 1000;
    const isPartialOfferPossible = diffMs >= hoursToMs(MINIMUM_PARTIAL_SHIFT_HOURS);

    const formatDateForMoment = (dateStr: string, timeStr: string): string => {
        const formattedDate = dateStr.trim();
        const formattedTime = timeStr.trim();
        return `${formattedDate} ${formattedTime}`;
    };

    const jobStartDateFormatted = formatDateForMoment(job.startDate, job.shifts[0].postedStart);
    
    const createSafariCompatibleDate = (dateTimeStr: string) => {
        const [datePart, timePart] = dateTimeStr.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);
        return new Date(year, month - 1, day, hours, minutes);
    };
    
    const localJobStartDate = createSafariCompatibleDate(jobStartDateFormatted);
    const jobStartDate = moment(localJobStartDate);
    const now = moment();
    
    const hoursUntilShift = jobStartDate.diff(now, 'hours', true);
    const isSameDay = jobStartDate.isSame(now, 'day');
    const isWithinTimeWindow = (!isNaN(hoursUntilShift) && hoursUntilShift <= PARTIAL_OFFER_TIME_WINDOW) || isSameDay;

    const handleInfoClick = () => {
        setShowTooltip(!showTooltip);
    };

    const renderPartialOfferButton = () => {
        const mobileButtonStyle = { 
            margin: '0 auto 1rem auto',
            display: 'block',
            width: '100%',
            maxWidth: '280px'
        };

        if (!isWithinTimeWindow) {
            if (isMobileApp) {
                return (
                    <div className={styles.tooltipContainer}>
                        <OutlinedButton
                            variant="outlined"
                            size="large"
                            disabled
                            style={{ 
                                ...mobileButtonStyle,
                                opacity: 0.6
                            }}
                            className="no-fastclick"
                        >
                            Offer to Work Part of the Shift
                        </OutlinedButton>
                        <div 
                            onClick={handleInfoClick} 
                            className={styles.infoIconWrapper}
                        >
                            <InfoIcon color="primary" fontSize="small" />
                        </div>
                        {showTooltip && (
                            <div className={styles.mobileTooltip}>
                                {PARTIAL_OFFER_TOOLTIP_TEXT}
                            </div>
                        )}
                    </div>
                );
            } else {
                return (
                    <Tooltip 
                        title={PARTIAL_OFFER_TOOLTIP_TEXT} 
                        placement="top"
                        arrow
                    >
                        <div>
                            <OutlinedButton
                                variant="outlined"
                                size="large"
                                disabled
                                style={{ marginBottom: '1rem', opacity: 0.6 }}
                                className="no-fastclick"
                            >
                                Offer to Work Part of the Shift
                            </OutlinedButton>
                        </div>
                    </Tooltip>
                );
            }
        }

        return (
            <OutlinedButton
                variant="outlined"
                size="large"
                onClick={() => setIsOpenPartialOfferModal(true)}
                style={isMobileApp ? mobileButtonStyle : { marginBottom: '1rem' }}
                className="no-fastclick"
            >
                Offer to Work Part of the Shift
            </OutlinedButton>
        );
    };

    const OfferShiftPopup: FC<OfferShiftPopupProps> = ({proposedRate, onSubmit}) => {
        const from = job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].postedStart;
        const to = job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].postedEnd;
        
        return (
            <div className={styles.popupContainer}>
                <div className={styles.titleContainer}>
                    <h2 className={styles.popupTitle__confirm}>Nicely Done! Your Offer is Sent.</h2>
                    <p className={styles.popupSubtitle}>
                        Important! Read Carefully
                    </p>
                </div>
                <div className={styles.contentContainer}>
                    <div className={styles.modalDescription}>
                        <span className={styles.emphasizedText}>We've notified {job.location.practiceName} of your offer to work their shift.</span>
                    </div>
                    <div className={styles.modalDescription}>
                        They will have 18hrs to accept your Offer.
                    </div>
                    <div className={styles.modalDescription}>
                        <span className={styles.emphasizedText}>Keep a close eye on your email inbox and phone.</span>
                    </div>
                    <div className={styles.modalDescription}>
                        If your offer is accepted, we'll notify you immediately by email and text message.
                    </div>
                    <div className={styles.shiftDescriptionWrapper}>
                        <ShiftDescriptionContainer
                            header={job.location.practiceName}
                            address={job.location.address}
                            from={from}
                            to={to}
                            rate={job?.ghr || proposedRate}
                            isConfirmModal={true}
                            isGuaranteedRate={!!job?.ghr}
                        />
                    </div>
                </div>

                <ContainedButton
                    className={clsx(styles.modalButton, 'no-fastclick')}
                    variant="contained"
                    size="large"
                    onClick={() => proposedRate && onSubmit(proposedRate)}
                >
                    Agreed & Commited
                </ContainedButton>
            </div>
        );
    }

    return (<>
        <Popup opened={isOpenCDHOCertificateModal} onClose={() => setIsOpenCDHOCertificateModal(false)}>
            <UpdateCDHOCertificate
                onClose={() => setIsOpenCDHOCertificateModal(false)}
                hygienist={hygienist}
                />
        </Popup>
        <Popup opened={isOpenMakeOfferModal} onClose={() => setIsOpenMakeOfferModal(false)}>
            <MakeAnOfferPopup
                onClose={() => setIsOpenMakeOfferModal(false)}
                hygienist={hygienist}
                job={job}
                proposedRate={proposedRate}
                setProposedRate={setProposedRate}
                isOpenOfferShiftPopup={isOpenOfferShiftPopup}
                setIsOpenOfferShiftPopup={setIsOpenOfferShiftPopup}
                isMaxBookedPopupOpen={isMaxBookedPopupOpen}
                setIsMaxBookedPopupOpen={setIsMaxBookedPopupOpen}

                />
        </Popup>
        <Popup opened={isOpenPartialOfferModal} onClose={() => setIsOpenPartialOfferModal(false)}>
            <MakePartialOfferPopup
                hygienist={hygienist}
                job={job}
                onClose={() => setIsOpenPartialOfferModal(false)}
                onSubmit={onMakePartialOffer}
            />
        </Popup>
        <Popup opened={isOpenOfferShiftPopup}>
            <OfferShiftPopup
                proposedRate={proposedRate}
                onSubmit={handleOfferShiftSubmit}
            />
        </Popup>

        <Popup opened={isOpenHideOfferModal} onClose={() => setIsOpenHideOfferModal(false)}>
            <ShiftPopup
                title={'Hide Shift?'}
                submit={'Hide Shift'}
                close={'Cancel'}
                job={job}
                onClose={() => setIsOpenHideOfferModal(false)}
                onSubmit={onHideOffer}
            />
        </Popup>
            <MaxBookedPopup
                isOpen={isMaxBookedPopupOpen}
                onClose={() => {
                    setIsMaxBookedPopupOpen(false);
                }}
                hygienistStatus={hygienistStatus}
                completedShifts={completedShifts}
            />
            <ContainedButton
                variant="contained"
                size="large"
                onClick={sendOfferHandler}
                className="no-fastclick"
            >   
                Send an Offer at Your Hourly Rate
            </ContainedButton>

        <Show if={isPartialOfferPossible}>
            <PartialOfferSubtitle>Can't work the whole shift?</PartialOfferSubtitle>
            {renderPartialOfferButton()}
        </Show>

        <Description>NOTE: All temping offers and bookings must be sent through the TempStars service, do not call them.</Description>

        <StyledButton
            onClick={() => setIsOpenHideOfferModal(true)}
            sx={{ 
                color: 'var(--primary-blue)',
                background: 'none',
                border: 'none',
                boxShadow: 'none',
                minWidth: 'auto',
                padding: '4px 8px',
                gap: '4px',
                '&:hover': {
                    background: 'none',
                    boxShadow: 'none'
                }
            }}
            size="small"
            variant="text"
            className="no-fastclick"
        >
            Hide This Shift <VisibilityOffIcon sx={{ fontSize: 18 }} />
        </StyledButton>

        <FixedLoader
            loading={makePartialOfferLoading || hideOfferLoading}
        />
    </>)
}

export default MakeOfferModal;